export type AppEnvironment = 'development' | 'staging' | 'production';

/**
 * Every deployed environment serves the whole API from a single origin, split by path — `/rest`
 * (main worker), `/wordlists` (wordlist worker), `/webhooks/paddle`. So the only thing that varies
 * between environments is the origin, and deriving it from `VITE_ENVIRONMENT` is strictly safer
 * than configuring one URL per service: a `VITE_WORKER_API_URL` that arrived empty used to fall
 * back to the production API, which is how a staging deploy quietly talked to prod.
 *
 * Local dev is the one environment where the two workers are genuinely separate — they run as two
 * `wrangler dev` processes on their own ports.
 */
const API_ORIGINS: Record<AppEnvironment, {worker: string; wordlist: string}> = {
  development: {worker: 'http://127.0.0.1:8787', wordlist: 'http://127.0.0.1:8788'},
  staging: {worker: 'https://stagingapi.censorme.app', wordlist: 'https://stagingapi.censorme.app'},
  production: {worker: 'https://api.censorme.app', wordlist: 'https://api.censorme.app'},
};

/**
 * The static-builds-worker is a separate, unrelated Cloudflare Worker (installer binaries, not
 * `/rest` or `/wordlists`), so it gets its own origin map rather than a third field on
 * `API_ORIGINS`. There is no local build of it — it's public and read-only, so pointing
 * development at staging is safe and avoids a dev build silently resolving to production.
 */
const BUILDS_ORIGINS: Record<AppEnvironment, string> = {
  development: 'https://stagingmapper.censorme.app',
  staging: 'https://stagingmapper.censorme.app',
  production: 'https://mapper.censorme.app',
};

/**
 * The one switch every environment-dependent value hangs off. Unrecognised values throw rather than
 * defaulting: silently picking an environment is what sent staging traffic to the production API.
 */
export function getEnvironment(): AppEnvironment {
  const raw = (import.meta.env.VITE_ENVIRONMENT || '').trim().toLowerCase();

  switch (raw) {
    case 'development':
    case 'dev':
    case 'local':
      return 'development';
    case 'staging':
    case 'sandbox':
      return 'staging';
    case 'production':
    case 'prod':
      return 'production';
    default:
      throw new Error(
        `VITE_ENVIRONMENT is ${raw ? `'${raw}'` : 'unset'}; expected development, staging or production.`
      );
  }
}

/** Base URL of the main API worker. Endpoints hang off `/rest/v1/…`. */
export function getWorkerApiUrl(): string {
  return API_ORIGINS[getEnvironment()].worker;
}

/** Base URL of the wordlist worker. Endpoints hang off `/wordlists` and `/user/wordlists`. */
export function getWordlistApiUrl(): string {
  return API_ORIGINS[getEnvironment()].wordlist;
}

/** Base URL of the static builds worker. Endpoints are `GET /windows`, `/mac-arm64`, `/mac-x64`. */
export function getBuildsApiUrl(): string {
  return BUILDS_ORIGINS[getEnvironment()];
}

/**
 * Thin wrapper over the browser `fetch`.
 *
 * The desktop app routed every request through Tauri's HTTP plugin, which issues requests from the
 * Rust side and therefore bypasses CORS entirely. In the browser these are ordinary cross-origin
 * requests, so every endpoint must send permissive CORS headers — see docs/BACKEND_REQUESTS.md.
 *
 * The response body is buffered once and memoised so that callers can safely call both `.json()`
 * and `.text()` on the same response, which a raw `Response` does not allow.
 */
export async function apiFetch(url: string, options?: {
    method?: string;
    headers?: Record<string, string>;
    body?: BodyInit | null;
    signal?: AbortSignal;
}): Promise<{
    ok: boolean;
    status: number;
    headers: Headers;
    json: () => Promise<any>;
    text: () => Promise<string>;
}> {
  const response = await fetch(url, {
    method: options?.method || 'GET',
    headers: options?.headers || {},
    body: options?.body,
    signal: options?.signal,
  });

    // Buffer the body once so json()/text() can both be called by the caller.
  const responseText = await response.text();

  return {
    ok: response.ok,
    status: response.status,
    headers: response.headers,
    json: async () => JSON.parse(responseText),
    text: async () => responseText,
  };
}
